<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import { useBase, dataHora, MONO } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186 — uma voz do Bira, de um dia.
//
// A tela do Bira virou a lista de versões da voz; esta é a que se abre ao clicar numa delas. Ela
// existe para responder "o que mudou", e não só "o que estava escrito": por isso o texto daquele
// dia vem com o da voz anterior ao lado, e não sozinho.
const { t } = useI18n();
const route = useRoute();
const { irPara } = useBase();

const CAMPOS = ['tom', 'diretrizes', 'handoff'];

const carregando = ref(true);
const versao = ref(null);
const anterior = ref(null);
const restaurando = ref(false);

const mudou = computed(() => versao.value?.campos || []);

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await SandboxAPI.comportamento(route.params.id);
    versao.value = data.versao;
    anterior.value = data.anterior;
  } catch (error) {
    useAlert(t('BIRA_VOZ.ERRO'));
  } finally {
    carregando.value = false;
  }
};

// Restaura só o jeito de falar: as respostas do rascunho ficam como estão. É a razão de esta
// tela existir separada da versão da base, onde restaurar significa a base inteira.
const trazerDeVolta = async () => {
  restaurando.value = true;
  try {
    await SandboxAPI.restaurar(versao.value.versao_id, 'comportamento');
    useAlert(t('BIRA_VOZ.TROUXE'));
    irPara('capitao_base_comportamento');
  } catch (error) {
    useAlert(t('BIRA_VOZ.ERRO_RESTAURAR'));
  } finally {
    restaurando.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BIRA_VOZ.HEADER')"
  >
    <template #body>
      <div
        v-if="versao"
        class="flex flex-col gap-3.5 w-full min-w-0 max-w-[1100px]"
      >
        <div class="flex items-baseline gap-2.5">
          <button
            type="button"
            class="text-[15px] text-n-slate-11 hover:text-n-slate-12"
            @click="irPara('capitao_calibracao')"
          >
            {{ $t('BIRA_VOZ.VOLTAR') }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ versao.rotulo || $t('BIRA_VOZ.HEADER') }}
          </h1>
          <span
            v-if="versao.no_ar"
            :class="MONO"
            class="bg-n-teal-3 text-n-teal-11"
          >
            {{ $t('BIRA_VOZ.NO_AR') }}
          </span>
        </div>

        <section
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[280px]">
            <p class="text-[13px] text-n-slate-11">
              {{
                versao.ate
                  ? $t('BIRA_VOZ.PERIODO', {
                      de: dataHora(versao.quando),
                      ate: dataHora(versao.ate),
                    })
                  : $t('BIRA_VOZ.DESDE', { de: dataHora(versao.quando) })
              }}
              <template v-if="versao.autor">
                · {{ $t('BIRA_VOZ.POR', { autor: versao.autor }) }}
              </template>
            </p>
            <p v-if="mudou.length" class="mt-1 text-[12.5px] text-n-amber-11">
              {{
                $t('BIRA_VOZ.MUDOU', {
                  campos: mudou
                    .map(campo => $t(`BIRA_VOZ.CAMPO.${campo.toUpperCase()}`))
                    .join(', '),
                })
              }}
            </p>
          </div>
          <Button
            v-if="versao.no_ar"
            :label="$t('BIRA_VOZ.EDITAR')"
            color="amber"
            size="sm"
            @click="irPara('capitao_base_comportamento')"
          />
          <Button
            v-else
            :label="$t('BIRA_VOZ.TRAZER')"
            variant="faded"
            color="amber"
            size="sm"
            :is-loading="restaurando"
            @click="trazerDeVolta"
          />
        </section>

        <!-- O texto daquele dia com o de antes ao lado: é o que responde "o que mudou". -->
        <section
          v-for="campo in CAMPOS"
          :key="campo"
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div
            class="px-[18px] py-3 border-b border-solid border-n-weak flex items-center gap-2.5"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t(`BIRA_VOZ.CAMPO.${campo.toUpperCase()}`) }}
            </h2>
            <span
              v-if="mudou.includes(campo)"
              :class="MONO"
              class="bg-n-amber-3 text-n-amber-11"
            >
              {{ $t('BIRA_VOZ.MUDOU_CHIP') }}
            </span>
          </div>
          <div class="flex items-stretch">
            <div
              v-if="anterior"
              class="w-[340px] flex-none px-5 py-4 border-r border-solid border-n-weak bg-n-alpha-1 flex flex-col gap-2"
            >
              <span
                class="font-mono text-[10px] uppercase tracking-[.06em] text-n-slate-10 whitespace-nowrap"
              >
                {{ $t('BIRA_VOZ.ANTES', { rotulo: anterior.rotulo }) }}
              </span>
              <p
                class="rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 py-3 text-[12.5px] leading-relaxed text-n-slate-11 whitespace-pre-line break-words max-h-[260px] overflow-y-auto"
              >
                {{ anterior.textos[campo] || $t('BIRA_VOZ.VAZIO') }}
              </p>
            </div>
            <div class="flex-1 min-w-0 px-5 py-4 flex flex-col gap-2">
              <span
                class="font-mono text-[10px] uppercase tracking-[.06em] text-n-amber-11 whitespace-nowrap"
              >
                {{ $t('BIRA_VOZ.NESTA') }}
              </span>
              <p
                class="rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 py-3 text-[13px] leading-relaxed text-n-slate-12 whitespace-pre-line break-words"
              >
                {{ versao.textos[campo] || $t('BIRA_VOZ.VAZIO') }}
              </p>
            </div>
          </div>
        </section>
      </div>

      <!-- Versão que não existe (link velho, versão descartada) não pode virar tela vazia: foi
           assim que a tela de publicar abriu preta em 16/09. -->
      <div
        v-else-if="!carregando"
        class="flex flex-col items-start gap-3 w-full max-w-[600px]"
      >
        <h1 class="text-lg font-semibold text-n-slate-12">
          {{ $t('BIRA_VOZ.NAO_ACHOU.TITULO') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('BIRA_VOZ.NAO_ACHOU.CORPO') }}
        </p>
        <Button
          :label="$t('BIRA_VOZ.NAO_ACHOU.VOLTAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="irPara('capitao_calibracao')"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
